import React, { useRef, useState, useCallback } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, Download } from 'lucide-react';

export const ZoomableImage = ({ src, alt = '', className = '', maxH = '70vh', testId = 'zoomable-image', onDownload }) => {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  const clamp = (s) => Math.min(6, Math.max(1, s));
  const zoomBy = useCallback((delta) => {
    setScale(prev => {
      const next = clamp(+(prev + delta).toFixed(2));
      if (next === 1) setPos({ x: 0, y: 0 });
      return next;
    });
  }, []);
  const reset = () => { setScale(1); setPos({ x: 0, y: 0 }); };

  const onWheel = (e) => { e.preventDefault(); zoomBy(e.deltaY < 0 ? 0.4 : -0.4); };
  const onDouble = () => (scale > 1 ? reset() : setScale(2.5));

  const start = (x, y) => { drag.current = { x: x - pos.x, y: y - pos.y }; };
  const move = (x, y) => { if (drag.current && scale > 1) setPos({ x: x - drag.current.x, y: y - drag.current.y }); };
  const end = () => { drag.current = null; };

  // pinch
  const pinch = useRef(null);
  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) pinch.current = { d: dist(e.touches), s: scale };
    else if (e.touches.length === 1) start(e.touches[0].clientX, e.touches[0].clientY);
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinch.current) {
      setScale(clamp(pinch.current.s * (dist(e.touches) / pinch.current.d)));
    } else if (e.touches.length === 1) move(e.touches[0].clientX, e.touches[0].clientY);
  };
  const onTouchEnd = () => { pinch.current = null; end(); if (scale <= 1) setPos({ x: 0, y: 0 }); };

  return (
    <div className="relative">
      <div
        className={`overflow-hidden bg-black rounded-lg select-none ${scale > 1 ? 'cursor-grab active:cursor-grabbing' : 'cursor-zoom-in'} ${className}`}
        style={{ maxHeight: maxH, touchAction: 'none' }}
        onWheel={onWheel}
        onDoubleClick={onDouble}
        onMouseDown={(e) => { e.preventDefault(); start(e.clientX, e.clientY); }}
        onMouseMove={(e) => move(e.clientX, e.clientY)}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        data-testid={testId}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="w-full object-contain"
          style={{ maxHeight: maxH, transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`, transformOrigin: 'center center', transition: drag.current ? 'none' : 'transform 120ms ease-out' }}
        />
      </div>
      <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur rounded-full px-1.5 py-1">
        <button type="button" onClick={() => zoomBy(-0.6)} className="w-7 h-7 rounded-full hover:bg-white/15 flex items-center justify-center text-white" data-testid="zoom-out-btn" aria-label="Alejar"><ZoomOut size={14}/></button>
        <span className="text-white text-[11px] tabular-nums w-10 text-center">{Math.round(scale * 100)}%</span>
        <button type="button" onClick={() => zoomBy(0.6)} className="w-7 h-7 rounded-full hover:bg-white/15 flex items-center justify-center text-white" data-testid="zoom-in-btn" aria-label="Acercar"><ZoomIn size={14}/></button>
        <button type="button" onClick={reset} className="w-7 h-7 rounded-full hover:bg-white/15 flex items-center justify-center text-white" data-testid="zoom-reset-btn" aria-label="Restablecer"><RotateCcw size={12}/></button>
        {onDownload && <button type="button" onClick={onDownload} className="w-7 h-7 rounded-full hover:bg-white/15 flex items-center justify-center text-white" data-testid="photo-download-btn" aria-label="Descargar foto"><Download size={14}/></button>}
      </div>
    </div>
  );
};

export default ZoomableImage;
